'use client'

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { CalendarDays, Medal, Trophy } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useIsVisible } from '@/lib/hooks/useIsVisible'
import { TeamLink } from '@/components/ui/team-link'
import {
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableHead,
  StyledTableHeader,
  StyledTableRow,
} from '@/components/ui/styled-table'

interface PositionCount {
  positionCode: string
  count: number
  limit: number
  exceeded: boolean
}

interface PlayoffTeam {
  id: number
  name: string | null
  ownerName: string
  points: number
  spGames: number
  seed: number | null
  eligible: boolean
  penaltyOverall: number | null
  hitterGames: PositionCount[]
}

export interface PlayoffBracket {
  id: number
  name: string
  displayName: string
  year: number
  draftYear: number
  rules: {
    id: number
    seedStart: number
    seedEnd: number
    firstRoundPickStart: number
    firstRoundPickEnd: number
    supplementalOverall: number | null
    supplementalRound: number | null
    maxSpGames: number
    maxHitterGames: number
  }
  rounds: Array<{
    id: number
    name: string | null
    roundNum: number
    startDate: string
    endDate: string
    maxSpGames: number
    maxHitterGames: number
    matchups: Array<{
      id: number
      matchupSlot: number | null
      status: string | null
      locked: boolean
      team1Id: number | null
      team2Id: number | null
      team1SourceRoundId: number | null
      team1SourceOutcome: string | null
      team2SourceRoundId: number | null
      team2SourceOutcome: string | null
      teams: PlayoffTeam[]
    }>
  }>
  standings: Array<{ teamId: number; teamName: string | null; points: number; place: number }>
  awards: Array<{
    id: number
    draftYear: number
    masterTeamId: number
    awardType: string
    round: number
    pick: number | null
    overall: number
    source: string | null
    points: number | null
    applied: boolean
  }>
}

interface PlayoffsResponse {
  year: number
  brackets: PlayoffBracket[]
}

function formatDate(value: string) {
  return value.slice(0, 10)
}

function formatPoints(points: number) {
  return points.toFixed(2)
}

function YearControls({ year, setYear }: { year: number; setYear: (year: number) => void }) {
  return (
    <div className="flex items-end gap-3">
      <div className="space-y-1">
        <Label htmlFor="playoff-year">Year</Label>
        <Input
          id="playoff-year"
          data-testid="playoff-year"
          className="w-28"
          type="number"
          value={year}
          onChange={event => setYear(parseInt(event.target.value, 10) || new Date().getFullYear())}
        />
      </div>
    </div>
  )
}

function PositionUsage({ positions }: { positions: PositionCount[] }) {
  return (
    <div className="mt-2 grid grid-cols-4 gap-1 text-[11px] sm:grid-cols-8">
      {positions.map(position => (
        <div
          key={position.positionCode}
          className={
            position.exceeded
              ? 'rounded border border-red-300 bg-red-50 px-1.5 py-1 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300'
              : 'rounded border bg-muted/40 px-1.5 py-1 text-muted-foreground'
          }
        >
          <div className="font-medium">{position.positionCode}</div>
          <div>
            {position.count}/{position.limit}
          </div>
        </div>
      ))}
    </div>
  )
}

export function BracketCard({ bracket }: { bracket: PlayoffBracket }) {
  return (
    <section className="space-y-4" data-testid={`playoff-bracket-${bracket.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">{bracket.displayName}</h2>
          <p className="text-sm text-muted-foreground">
            Seeds {bracket.rules.seedStart}-{bracket.rules.seedEnd} | Picks{' '}
            {bracket.rules.firstRoundPickStart}-{bracket.rules.firstRoundPickEnd}
            {bracket.rules.supplementalOverall
              ? ` | Supplemental #${bracket.rules.supplementalOverall}`
              : ''}
          </p>
        </div>
        <Badge variant="outline">Draft {bracket.draftYear}</Badge>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="grid gap-4 lg:grid-cols-2">
          {bracket.rounds.map(round => (
            <Card key={round.id}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-base">
                  <span>{round.name ?? `Round ${round.roundNum}`}</span>
                  <Badge variant="secondary">
                    {formatDate(round.startDate)} to {formatDate(round.endDate)}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  SP {round.maxSpGames}; hitters {round.maxHitterGames} per slot, OF{' '}
                  {round.maxHitterGames * 3}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {round.matchups.map(matchup => (
                  <div
                    key={matchup.id}
                    className="rounded-md border p-3"
                    data-testid={`playoff-matchup-${matchup.id}`}
                  >
                    <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                      <span>Matchup {matchup.matchupSlot ?? '-'}</span>
                      <span>
                        {matchup.status ?? 'PENDING'}
                        {matchup.locked ? ' | Override' : ''}
                      </span>
                    </div>
                    {matchup.teams.length ? (
                      matchup.teams.map(team => {
                        const highScore =
                          matchup.status === 'FINAL' &&
                          team.points === Math.max(...matchup.teams.map(entry => entry.points))
                        return (
                          <div key={team.id} className="border-t py-2 first:border-t-0 first:pt-0">
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <TeamLink
                                  teamId={team.id}
                                  className={highScore ? 'font-semibold' : undefined}
                                >
                                  {team.name ?? `Team ${team.id}`}
                                </TeamLink>
                                <div className="text-xs text-muted-foreground">
                                  Seed {team.seed ?? '-'}
                                </div>
                                {!team.eligible && (
                                  <Badge variant="destructive" className="mt-1">
                                    Ineligible — final draft pick #{team.penaltyOverall ?? '-'}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-right">
                                <div className={highScore ? 'text-lg font-semibold' : 'text-lg'}>
                                  {formatPoints(team.points)}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  SP {team.spGames}
                                </div>
                              </div>
                            </div>
                            <PositionUsage positions={team.hitterGames} />
                          </div>
                        )
                      })
                    ) : (
                      <div className="py-5 text-center text-sm text-muted-foreground">
                        Teams pending
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Trophy className="h-4 w-4" /> Bracket Standings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <StyledTable>
                <StyledTableHeader>
                  <StyledTableRow>
                    <StyledTableHead>Place</StyledTableHead>
                    <StyledTableHead>Team</StyledTableHead>
                    <StyledTableHead className="text-right">Pts</StyledTableHead>
                  </StyledTableRow>
                </StyledTableHeader>
                <StyledTableBody>
                  {bracket.standings.map((standing, index) => (
                    <StyledTableRow key={standing.teamId} index={index}>
                      <StyledTableCell>{standing.place}</StyledTableCell>
                      <StyledTableCell>
                        <TeamLink teamId={standing.teamId}>
                          {standing.teamName ?? `Team ${standing.teamId}`}
                        </TeamLink>
                      </StyledTableCell>
                      <StyledTableCell className="text-right font-mono">
                        {formatPoints(standing.points)}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </StyledTableBody>
              </StyledTable>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Medal className="h-4 w-4" /> Draft Awards
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {bracket.awards.length ? (
                bracket.awards.map(award => (
                  <div
                    key={award.id}
                    className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                  >
                    <span>
                      {award.awardType === 'SUPPLEMENTAL'
                        ? 'Supplemental'
                        : award.awardType === 'PENALTY'
                          ? 'Ineligible penalty'
                          : award.awardType === 'REGULAR_SEASON'
                            ? 'Regular-season pick'
                            : 'Pick'}{' '}
                      #{award.overall}
                    </span>
                    <span className="text-muted-foreground">
                      Team {award.masterTeamId}
                      {award.applied ? ' | applied' : ''}
                    </span>
                  </div>
                ))
              ) : (
                <div className="text-sm text-muted-foreground">
                  Awards generate when all final matchups are final.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  )
}

export function PlayoffsClient({ initialYear }: { initialYear: number }) {
  const [year, setYear] = useState(initialYear)
  const isVisible = useIsVisible()
  const { data, isLoading, error } = useQuery<PlayoffsResponse>({
    queryKey: ['playoffs', year],
    queryFn: async () => {
      const response = await fetch(`/api/playoffs?year=${year}`, { cache: 'no-store' })
      if (!response.ok) throw new Error('Failed to fetch playoffs')
      return response.json()
    },
    refetchInterval: isVisible ? 30000 : false,
    refetchOnWindowFocus: true,
    staleTime: 0,
  })

  const brackets = useMemo(() => data?.brackets ?? [], [data])

  return (
    <div className="container mx-auto max-w-full px-4 py-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Trophy className="h-6 w-6" /> Playoffs
          </h1>
          <p className="text-sm text-muted-foreground">
            Live bracket scoring, position limits, standings, and draft awards.
          </p>
        </div>
        <YearControls year={year} setYear={setYear} />
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}
      {isLoading && (
        <div className="rounded-md border p-8 text-center text-muted-foreground">
          Loading playoffs...
        </div>
      )}
      {!isLoading && !brackets.length && (
        <Card>
          <CardContent className="flex items-center gap-3 p-6 text-muted-foreground">
            <CalendarDays className="h-5 w-5" />
            No playoff setup exists for {year}.
          </CardContent>
        </Card>
      )}

      <div className="space-y-8">
        {brackets.map(bracket => (
          <BracketCard key={bracket.id} bracket={bracket} />
        ))}
      </div>
    </div>
  )
}
